import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MAChristianMinistry = () => {
  const coreCourses = [
    { code: 'MIN 503', name: 'Advanced Theology of Ministry', credits: 3 },
    { code: 'MIN 505', name: 'Advanced Contemporary Preaching and Teaching', credits: 3 },
    { code: 'MIN 507', name: 'Leadership in the Church', credits: 3 },
    { code: 'MIN 509', name: '21st Century Evangelism, Discipleship, and Church Growth', credits: 3 },
    { code: 'MIN 602', name: 'Church Administration', credits: 3 },
    { code: 'MIN 606', name: 'Recruiting and Training Volunteers', credits: 3 },
    { code: 'MIN 615', name: 'Use of Small Groups in Church Building', credits: 3 }
  ];

  const electiveAreas = [
    {
      area: 'Christian Counseling/Psychology',
      courses: [{ code: 'MIN 521', name: 'Advanced Christian Counseling', credits: 3 }]
    },
    {
      area: 'Christian Education',
      courses: [{ code: 'MIN 533', name: 'Developing a Total Church Education Program', credits: 3 }]
    },
    {
      area: 'Christian Missions',
      courses: [{ code: 'MIN 541', name: 'Strategies for Global Missions', credits: 3 }]
    }
  ];

  const programFeatures = [
    {
      title: "Practical Ministry Skills",
      description: "Develop essential skills for effective church leadership and ministry",
      icon: "🎯"
    },
    {
      title: "Contemporary Focus",
      description: "Address modern challenges in church growth and evangelism",
      icon: "🌍"
    },
    {
      title: "Leadership Development",
      description: "Build strong leadership and administrative capabilities",
      icon: "👥"
    },
    {
      title: "Hands-on Experience",
      description: "Apply learning through practical ministry situations",
      icon: "✋"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Christian Ministry
            </h1>
            <p className="text-xl opacity-90">
              Advanced training for effective church leadership and ministry
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The Master of Arts in Christian Ministry (MACM) program is designed to offer 
                practical help to those already in church ministry, deepening their skills 
                in communication, leadership and ministry while providing new insights for 
                effective church leadership in today's changing world.
              </p>
              <p className="text-gray-600">
                This program is ideal for ministry professionals seeking to enhance their 
                ministerial skills and those entering ministry who desire practical, 
                contemporary training.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (21 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Areas */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Elective Areas (9 credits)</h2>
            <div className="space-y-6">
              {electiveAreas.map((area, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8">
                  <h3 className="text-xl font-semibold mb-4 text-primary-600">
                    {area.area}
                  </h3>
                  <div className="divide-y divide-gray-200">
                    {area.courses.map((course, courseIndex) => (
                      <div key={courseIndex} className="py-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {course.code}: {course.name}
                            </h4>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                            {course.credits} Credits
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Ministry Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of ministry leaders and enhance your ability to serve effectively.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MAChristianMinistry; 